import store from '../../../store/store';
import { BaseComponent } from "../../base-component/base-component";
import { ErrorToastWishList } from '../constants/wish-list-error-toast-status';
import { WishListAnalytics } from '../utils/wish-list-analytics.util';
import { booleanConverter } from '../../../common/converters';
import { customElement, property, state } from 'lit/decorators.js';
import { deleteWishList } from '../thunks/wish-list-delete.thunk';
import { fetchAllWishLists } from '../thunks/wish-list-fetch-all-wish-lists.thunks';
import { html, nothing, render } from 'lit';
import { isDeleteWishListSuccess, redirectToMyLists } from '../utils/wish-list.util';
import { smallSpinner } from '../../../common/constants/api-constants';
import { triggerCloseOffCanvas } from '../../off-canvas/store/off-canvas.store';
import {
  getGlobalToastContainer,
  renderDeleteWishListSuccessToast,
  renderErrorToast
} from '../utils/wish-list-toast.util';
import { UserProfile } from '../../user-profile/user-profile.interface';
import { StoreState } from '../../../store/store.interface';
import { selectUser } from '../../user-profile/user-profile.selectors';
import { connect } from 'pwa-helpers';

@customElement('mhecomm-delete-wish-list')
export class DeleteWishList extends connect(store)(BaseComponent) {
  /**
   * The label of the cancel button.
   */
  @property()
  cancelButtonName = 'Cancel';

  /**
   * The label of the delete button.
   */
  @property()
  deleteButtonName = 'Delete';

  /**
   * The disclaimer message to show before deleting a wish list.
   */
  @property()
  disclaimer = 'You cannot undo list deletion, so only delete a list if you’re sure you won’t need to reference it again.';

  /**
   * If the component is used in the wish list item page, the component will redirect after deletion.
   */
  @property({ converter: booleanConverter })
  isWishListItemPage = false;

  /**
   * If the component is used as an off-canvas, this should be set to the off-canvas ID.
   */
  @property()
  offCanvasId = '';

  /**
   * The ID of the wish list to delete.
   */
  @property({type: Number})
  wishListId = 0;

  /**
   * The name of the wish list to delete.
   */
  @property()
  wishListName = '';

  /**
   * A flag to indicate if the delete wish list action is being processed.
   */
  @state()
  private isDeleteWishListPending = false;

  @state()
  private user: UserProfile | undefined;

  /**
   * The analytics instance for the wish list.
   */
  private readonly analytics: WishListAnalytics;

  constructor() {
    super();
    this.analytics = new WishListAnalytics();
  }

  stateChanged(state: StoreState): void {
    super.stateChanged(state);
    this.user = selectUser(state);
  }

  renderComp() {
    if (!this.wishListId) {
      return nothing;
    }

    return html`
      <div>
        <div class="pb-3">
          <span id="wish-list-deletion-disclaimer">
            ${this.disclaimer}
          </span>
        </div>
        <div class="d-flex flex-column border-top py-3">
          <button
            id="delete-wish-list-${this.wishListId}-cancel"
            data-automation-id="delete-wish-list-${this.wishListId}-cancel"
            class="btn btn-primary mb-2"
            aria-label="Cancel"
            @click=${this.onClickCancel}
          >
            ${this.cancelButtonName}
          </button>
          <button
            id="delete-wish-list-${this.wishListId}"
            data-automation-id="delete-wish-list-${this.wishListId}"
            class="btn btn-outline-primary"
            @click=${this.onClickDeleteWishList}
            ?disabled=${this.isDeleteWishListPending}
          >
            ${this.isDeleteWishListPending ? smallSpinner() : this.deleteButtonName}
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Handled the click event for the cancel button.
   */
  private onClickCancel(): void {
    this.analytics.deleteWishList(this, this.wishListId, this.wishListName, this.user!, this.cancelButtonName, true);
    this.closeOffCanvasIfExist();
  }

  /**
   * Deletes a wish list.
   */
  private onClickDeleteWishList(): void {
    this.analytics.deleteWishList(this, this.wishListId, this.wishListName, this.user!, this.deleteButtonName);
    this.isDeleteWishListPending = true;

    store.dispatch(deleteWishList(this.wishListId))
      .then(this.handleResponse.bind(this))
      .catch((): void => {
        this.displayToast(true);
      })
      .finally((): void => {
        this.isDeleteWishListPending = false;
      });
  }

  /**
   * Cancels the deletion of a wish list.
   */
  private closeOffCanvasIfExist(): void {
    if (this.offCanvasId) {
      store.dispatch(triggerCloseOffCanvas({offCanvasId: this.offCanvasId}));
    }
  }

  /**
   * Handles the response from deleting a wish list.
   */
  private handleResponse(): void {
    if (!isDeleteWishListSuccess()) {
      return this.displayToast(true);
    }

    if (this.isWishListItemPage) {
      return redirectToMyLists();
    }

    store.dispatch(fetchAllWishLists());
    this.closeOffCanvasIfExist();

    return this.displayToast(false, this.wishListName);
  }

  /**
   * Displays a toast depending on the wish list deletion status.
   */
  private displayToast(isError: boolean, wishListName?: string): void {
    const toastGlobalContainer: HTMLElement | undefined = getGlobalToastContainer();
    if (!toastGlobalContainer) {
      return;
    }

    if (isError) {
      render( renderErrorToast({
        error: ErrorToastWishList.DELETING_WISH_LIST,
        displayLoadingTemplate: true,
        autoHide: false,
        hideClose: true,
      }), toastGlobalContainer);
      return;
    }

    render(renderDeleteWishListSuccessToast(wishListName), toastGlobalContainer);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-delete-wish-list': DeleteWishList;
  }
}
